import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
    Box,
    Button,
    Typography,
    Alert,
    AlertTitle,
    LinearProgress,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    IconButton,
    Skeleton,
    Link
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Delete as DeleteIcon,
    InsertDriveFile as FileIcon,
    DateRange as DateRangeIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';

const PayoneerCSVUpload = ({ connectorId, onUploadComplete }) => {
    const { confirmDialogProps, confirm } = useConfirmDialog();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [loadingDateRange, setLoadingDateRange] = useState(true);
    const [deleting, setDeleting] = useState(false);

    // Fetch existing transaction date range on mount
    useEffect(() => {
        const fetchDateRange = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/connectors/payoneer/${connectorId}/date-range`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                setDateRange(response.data);
            } catch (err) {
                console.error('Error fetching date range:', err);
            } finally {
                setLoadingDateRange(false);
            }
        };

        if (connectorId) {
            fetchDateRange();
        }
    }, [connectorId]);

    const handleDeleteAllData = async () => {
        if (!await confirm({ title: 'Delete All Transactions', message: 'This will delete ALL transactions for this connector. This cannot be undone.', confirmText: 'Delete All', severity: 'danger' })) {
            return;
        }

        setDeleting(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/connectors/payoneer/${connectorId}/transactions`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                // Refresh date range to show empty state
                const dateRangeResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/connectors/payoneer/${connectorId}/date-range`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                setDateRange(dateRangeResponse.data);
                setUploadResults({ message: 'All data deleted successfully. You can now re-upload with corrected files.' });
            }
        } catch (err) {
            console.error('Delete error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to delete transactions');
        } finally {
            setDeleting(false);
        }
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(prevFiles => [...prevFiles, ...files]);
        setError(null);
        setUploadResults(null);
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            setError('Please select at least one CSV file to upload');
            return;
        }

        setUploading(true);
        setError(null);
        setUploadResults(null);

        try {
            const formData = new FormData();
            formData.append('connectorId', connectorId);

            selectedFiles.forEach(file => {
                formData.append('csvFiles', file);
            });

            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/connectors/payoneer/upload-csv`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setUploadResults(response.data);
            setSelectedFiles([]); // Clear file selection after successful upload

            // Refresh date range
            const dateRangeResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/connectors/payoneer/${connectorId}/date-range`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setDateRange(dateRangeResponse.data);

            if (onUploadComplete) {
                onUploadComplete(response.data);
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to upload files');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box>
            {/* Instructions */}
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
                <AlertTitle>How to download Payoneer CSV</AlertTitle>
                <Typography variant="body2" component="div">
                    1. Log in to your Payoneer account at{' '}
                    <Link href="https://www.payoneer.com" target="_blank" rel="noopener">
                        payoneer.com
                    </Link>
                    <br />
                    2. Navigate to <strong>Activity → Transactions</strong>
                    <br />
                    3. Select the date range you want to export
                    <br />
                    4. Click <strong>Export</strong> button and choose <strong>CSV</strong> format
                    <br />
                    5. Save the file to your computer and upload it here
                </Typography>
            </Alert>

            {/* Existing Data Summary */}
            {loadingDateRange ? (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                </Paper>
            ) : dateRange && dateRange.hasData ? (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DateRangeIcon color="success" />
                            <Box>
                                <Typography variant="body1" fontWeight={600}>
                                    Existing Data
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {dateRange.totalTransactions} transactions
                                    {' from '}
                                    {new Date(dateRange.minDate).toLocaleDateString()}
                                    {' to '}
                                    {new Date(dateRange.maxDate).toLocaleDateString()}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            onClick={handleDeleteAllData}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting...' : 'Delete All'}
                        </Button>
                    </Box>
                </Paper>
            ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    No transaction data uploaded yet. Upload your first CSV file below.
                </Alert>
            )}

            {/* File Selection */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Upload CSV Files
                </Typography>

                <input
                    type="file"
                    accept=".csv"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="payoneer-csv-upload"
                />

                <label htmlFor="payoneer-csv-upload">
                    <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        fullWidth
                        sx={{ mb: 2 }}
                    >
                        Select CSV Files (up to 10)
                    </Button>
                </label>

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                    <List dense>
                        {selectedFiles.map((file, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleRemoveFile(index)}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                }
                            >
                                <ListItemIcon>
                                    <FileIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={file.name}
                                    secondary={`${(file.size / 1024).toFixed(2)} KB`}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploading || selectedFiles.length === 0}
                    fullWidth
                    sx={{ mt: 2 }}
                    startIcon={uploading ? null : <UploadIcon />}
                >
                    {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
                </Button>

                {uploading && <LinearProgress sx={{ mt: 2 }} />}
            </Paper>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    <AlertTitle>Upload Error</AlertTitle>
                    {error}
                </Alert>
            )}

            {/* Upload Results */}
            {uploadResults && uploadResults.success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    <AlertTitle>Upload Successful!</AlertTitle>
                    <Typography variant="body2" gutterBottom>
                        <strong>Total transactions imported:</strong> {uploadResults.totalTransactions}
                        <br />
                        <strong>Files processed:</strong> {uploadResults.filesProcessed}
                    </Typography>

                    {uploadResults.files && uploadResults.files.length > 0 && (
                        <List dense sx={{ mt: 1 }}>
                            {uploadResults.files.map((file, idx) => (
                                <ListItem key={idx} sx={{ pl: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        {file.success ? (
                                            <SuccessIcon color="success" fontSize="small" />
                                        ) : (
                                            <ErrorIcon color="error" fontSize="small" />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={file.filename}
                                        secondary={
                                            file.success
                                                ? `${file.transactions} transactions imported`
                                                : `Error: ${file.error}`
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Alert>
            )}

            {/* CSV Format Info */}
            <Alert severity="info" icon={<InfoIcon />}>
                <AlertTitle>CSV Format Requirements</AlertTitle>
                <Typography variant="body2" component="div">
                    Your Payoneer CSV should include these columns:
                    <br />• <strong>Date</strong> (e.g., "13 Jan, 2025")
                    <br />• <strong>Description</strong> (transaction details)
                    <br />• <strong>Amount</strong> (transaction amount)
                    <br />• <strong>Currency</strong> (USD, EUR, etc.)
                    <br />• <strong>Status</strong> (Completed, Pending, etc.)
                    <br />• <strong>Running Balance</strong> (account balance)
                    <br />• <strong>Transaction ID</strong> (unique identifier)
                </Typography>
            </Alert>
            <ConfirmDialog {...confirmDialogProps} />
        </Box>
    );
};

export default PayoneerCSVUpload;
